<template>
  <div class="wrapper">
    <div class="wrapper-header">
      <TabsWidget activeTab="nominations" @selected="switchTab"></TabsWidget>
      <div class="tab-options">
        <b-button type="is-primary" outlined @click="createNewSession">
          CREATE NOMINATION SESSION
        </b-button>
      </div>
    </div>

    <div class="wrapper-body">
      <!-- Sessions Panel (Left) -->
      <NominationsSessions :nomination-sessions="nomination_sessions" :selected-session="selected_session"
        :loading="loading" @session-selected="onSessionSelected" @refresh-sessions="getNominationSessions"
        @create-session="createNewSession" @duplicate-session="duplicateSession" @update-session="updateSession"
        @delete-session="deleteSession" />

      <!-- Categories Panel (Middle) -->
      <NominationsCategories v-if="selected_session" :session="selected_session" :nominees="session_nominees"
        @selected="onCategorySelected" @close="closeCategories" />
      <div v-else class="middle-panel-notfound">
        <widgetNotfound title="No Categories" subtitle="Select a nomination session to see the categories" />
      </div>

      <!-- Nominees Panel (Right) -->
      <NominationsParticipants v-if="selected_session && selected_category" :session="selected_session"
        :category="selected_category" :nominees="filtered_nominees" @update-nominee-status="handleNomineeStatusUpdate"
        @move-to-participants="handleMoveToParticipants" @delete-nominee="handleDeleteNominee"
        @close="closeParticipants" />
      <div v-else class="rigth-panel-notfound">
        <widgetNotfound v-if="!selected_session" title="No Nominees"
          subtitle="Select a nomination session to see the nominees" />
        <widgetNotfound v-else-if="selected_session && !selected_category" title="No Nominees"
          subtitle="Select a category to see the nominees" />
      </div>
    </div>

    <!-- Modals -->
    <addNewVotingSessionModal v-if="show_add_session_modal" :channel="channel_details" :value="selected_session_to_edit"
      @saved="onSessionSaved" @close="show_add_session_modal = false" />
  </div>
</template>

<script>
import TabsWidget from "./components/TabsWidget.vue";
import NominationsSessions from "./components/NominationsSessions.vue";
import NominationsCategories from "./components/NominationsCategories.vue";
import NominationsParticipants from "./components/NominationsParticipants.vue";
import addNewVotingSessionModal from "./modals/addNewVotingSessionModal.vue";

export default {
  components: {
    TabsWidget,
    NominationsSessions,
    NominationsCategories,
    NominationsParticipants,
    addNewVotingSessionModal,
  },
  data: () => ({
    nomination_sessions: [],
    selected_session: null,
    selected_category: null,
    session_nominees: [],
    filtered_nominees: [],
    channel_details: null,
    show_add_session_modal: false,
    selected_session_to_edit: null,
    loading: false,
  }),
  watch: {},
  mounted() {
    this.getChannelDetails();
    this.getNominationSessions();
  },
  methods: {
    getChannelDetails() {
      this.$store
        .dispatch("CHANNEL_GET_MY_DETAILS")
        .then((response) => {
          this.channel_details = response.data.return;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to load channel details",
            type: "is-danger",
          });
        });
    },

    getNominationSessions() {
      this.loading = true;
      this.$store
        .dispatch("VOTING_GET_MY_VOTING_SESSIONS")
        .then((response) => {
          // For now, show all sessions and filter for nomination phase if available
          const allSessions = response.data.return || [];
          this.nomination_sessions = allSessions.filter(session => session.phase === 'nomination');
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.error('Error loading sessions:', error); // Debug log
          this.$buefy.toast.open({
            message: "Failed to load nomination sessions",
            type: "is-danger",
          });
        });
    },

    onSessionSelected(session) {
      this.selected_session = session;
      this.selected_category = null;
      this.session_nominees = [];
      this.filtered_nominees = [];
    },

    onCategorySelected(category) {
      this.selected_category = category;
      this.getSessionNominees();
    },

    getSessionNominees() {
      if (!this.selected_session || !this.selected_category) return;

      this.$store
        .dispatch("VOTING_GET_SESSION_NOMINEES", {
          session_id: this.selected_session.id,
          category_id: this.selected_category.id,
        })
        .then((response) => {
          this.session_nominees = response.data.return || [];
          this.filtered_nominees = this.session_nominees;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to load nominees",
            type: "is-danger",
          });
        });
    },

    filterNomineesByCategory() {
      // This method is no longer needed since we load nominees for specific category
      this.filtered_nominees = this.session_nominees;
    },

    handleNomineeStatusUpdate(data) {
      this.$store
        .dispatch("VOTING_UPDATE_NOMINEE_STATUS", data)
        .then(() => {
          // Update local data
          const nominee = this.session_nominees.find(n => n.id === data.nominee_id);
          if (nominee) {
            nominee.status = data.status;
          }
          this.$buefy.toast.open({
            message: "Nominee status updated successfully",
            type: "is-success",
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to update nominee status",
            type: "is-danger",
          });
        });
    },

    handleMoveToParticipants(nomineeId) {
      this.$store
        .dispatch("VOTING_MOVE_NOMINEE_TO_PARTICIPANT", {
          nominee_id: nomineeId,
        })
        .then(() => {
          // Update local data
          const nominee = this.session_nominees.find(n => n.id === nomineeId);
          if (nominee) {
            nominee.status = "moved_to_participants";
          }
          this.$buefy.toast.open({
            message: "Nominee moved to participants successfully",
            type: "is-success",
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to move nominee to participants",
            type: "is-danger",
          });
        });
    },

    handleDeleteNominee(nomineeId) {
      this.$store
        .dispatch("VOTING_DELETE_NOMINEE", {
          nominee_id: nomineeId,
        })
        .then(() => {
          // Remove from local arrays
          this.session_nominees = this.session_nominees.filter(n => n.id !== nomineeId);
          this.filtered_nominees = this.filtered_nominees.filter(n => n.id !== nomineeId);
          this.$buefy.toast.open({
            message: "Nominee deleted successfully",
            type: "is-success",
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to delete nominee",
            type: "is-danger",
          });
        });
    },

    closeCategories() {
      this.selected_session = null;
      this.selected_category = null;
    },

    closeParticipants() {
      this.selected_category = null;
    },

    createNewSession() {
      this.selected_session_to_edit = null;
      this.show_add_session_modal = true;
    },

    updateSession(session) {
      this.selected_session_to_edit = session;
      this.show_add_session_modal = true;
    },

    duplicateSession(session) {
      this.$store
        .dispatch("VOTING_DUPLICATE_SESSION", {
          id: session.id,
        })
        .then(() => {
          this.getNominationSessions();
          this.$buefy.toast.open({
            message: "Session duplicated successfully",
            type: "is-success",
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to duplicate session",
            type: "is-danger",
          });
        });
    },

    deleteSession(session) {
      this.$store
        .dispatch("VOTING_DELETE_MY_VOTING_SESSION", {
          id: session.id,
        })
        .then(() => {
          this.selected_session = null;
          this.selected_category = null;
          this.getNominationSessions();
          this.$buefy.toast.open({
            message: "Session deleted successfully",
            type: "is-success",
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: "Failed to delete session",
            type: "is-danger",
          });
        });
    },

    onSessionSaved() {
      this.show_add_session_modal = false;
      this.getNominationSessions();
    },

    switchTab(type) {
      this.$emit("tab", type);
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  padding: 0px;
  display: flex;
  flex-direction: column;
  height: 100%;

  .wrapper-header {
    width: 100%;
    height: 60px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #ddd;
    padding-left: 6px;
    overflow-x: auto;

    .tabs-widget {
      display: flex;
      align-items: flex-end;
      white-space: nowrap;
      margin-right: 20px;

      .h-spacer {
        margin-right: 20px;
      }

      .tab-item {
        cursor: pointer;
        padding: 14px 4px;
        border-bottom: 3px solid transparent;

        &.active {
          color: var(--wecodefy-secondary-color, #9fc953);
          border-bottom: 3px solid var(--wecodefy-secondary-color, #9fc953);
        }
      }
    }

    .tab-options {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0px 10px;

      .h-spacer {
        margin-right: 10px;
      }
    }
  }

  .wrapper-body {
    height: calc(100% - 60px);
    display: flex;

    .middle-panel-notfound {
      height: 100%;
      width: 400px;
      overflow-y: auto;
      border-left: 1px solid #ddd;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 592px) {
        position: absolute;
        background: white;
        z-index: 10;
        top: 60px;
        right: 0;
        width: 350px;
        opacity: 0;
        pointer-events: none;
      }
    }

    .rigth-panel-notfound {
      width: calc(100% - 800px);
      height: 100%;
      border-left: 1px solid #ddd;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 1184px) {
        position: absolute;
        background: white;
        z-index: 10;
        top: 60px;
        right: 0;
        width: 350px;
        opacity: 0;
        pointer-events: none;
      }
    }
  }
}
</style>
